import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import queryString from 'query-string'

import { useAppSelector } from 'redux/hooks'
import { useFolderDrop } from 'components/folderPage/folderHooks'

import { FolderColumn, FolderColumnOption, SaturatedFolderColumnOptions } from 'redux/folders/initialState'
import { DeckCardInfoType } from 'types/search'
import { SubfolderFolderItemType } from 'types/folders'

import {
  AUTO_COLUMNS,
  generateSaturatedColumns,
  generateSaturatedHeaders,
} from 'components/folderPage/FolderTable/columnDefinitions.utils'

import styles from './dynmaicTableItems.module.scss'

type Props = {
  deckOrFolder: DeckCardInfoType | SubfolderFolderItemType
  type: 'deck' | 'folder'
  selected?: boolean
  onToggleSelected?: () => void
}

type OrderBy = {
  orderBy: string
  orderDirection: 'ascending' | 'descending'
}

const DynamicFolderTableItem = ({ deckOrFolder, selected = false, onToggleSelected, type }: Props) => {
  const autoColumns = useAppSelector(state => state.folders.autoColumns)
  const columnData = useAppSelector(state => state.folders.columnData)

  // Only use the drop hook if this row is a folder, otherwise return a no-op
  // This breaks if a component ever is converted from a folder <=> deck, but that should never happen
  const [{ isOver }, drop] =
    !deckOrFolder.colors && type === 'folder' ? useFolderDrop(deckOrFolder) : [{ isOver: false }, (noOp: any) => noOp]

  const visableColumns = autoColumns
    ? columnData.map(opt => ({ ...opt, enabled: AUTO_COLUMNS.includes(opt.key) }))
    : columnData

  const saturatedDeckOrFolderRow = generateSaturatedColumns(deckOrFolder, type, selected, onToggleSelected, autoColumns)

  return drop(
    <div className={`${styles.row} ${selected ? styles.selected : ''} ${isOver ? styles.droppableAbove : ''}`}>
      <TableContent columns={visableColumns} saturatedColumns={saturatedDeckOrFolderRow} />
    </div>,
  )
}

export default DynamicFolderTableItem

export const DynamiTableHeader = () => {
  const router = useRouter()

  const orderBy = `${router.query.collectionOrderBy || 'createdAt'}`
  const orderDirection = `${router.query.orderDirection || 'ascending'}` as 'ascending' | 'descending'

  const autoColumns = useAppSelector(state => state.folders.autoColumns)
  const columnData = useAppSelector(state => state.folders.columnData)

  const [initialRender, setInitialRender] = useState(true)
  const [localOrdering, setLocalOrdering] = useState<OrderBy>({
    orderBy,
    orderDirection,
  })

  const handleChangeSort = (sortBy: string) => {
    let orderDirection: 'ascending' | 'descending' = 'ascending'

    if (sortBy === localOrdering.orderBy && localOrdering.orderDirection === 'ascending') orderDirection = 'descending'

    setLocalOrdering({ orderBy: sortBy, orderDirection })
  }

  const handleChangeOrderByAndRefresh = (params: OrderBy) => {
    const queryParams = { ...router.query }

    delete queryParams.folderId // Not needed as a query parameter
    delete queryParams.page // When reordering always jump back to default page

    queryParams.orderBy = `${params.orderDirection === 'descending' ? '-' : ''}${params.orderBy}`

    router.push(`/folders/${router.query.folderId}/?${queryString.stringify(queryParams)}`)
  }

  useEffect(() => {
    if (initialRender) {
      setInitialRender(false)

      return
    }

    const debouncedSearch = setTimeout(() => handleChangeOrderByAndRefresh(localOrdering), 750)

    return () => clearTimeout(debouncedSearch)
  }, [localOrdering])

  const saturatedHeaderInfo = generateSaturatedHeaders(
    handleChangeSort,
    localOrdering.orderBy,
    localOrdering.orderDirection,
    autoColumns,
  )

  const visableColumns = autoColumns
    ? columnData.map(opt => ({ ...opt, enabled: AUTO_COLUMNS.includes(opt.key) }))
    : columnData

  return (
    <div className={styles.header}>
      <TableContent columns={visableColumns} saturatedColumns={saturatedHeaderInfo} />
    </div>
  )
}

const TableContent = ({
  columns,
  saturatedColumns,
}: {
  columns: FolderColumnOption[]
  saturatedColumns: Partial<Record<FolderColumn, SaturatedFolderColumnOptions>>
}) => (
  <>
    {columns.map((column, index) => {
      if (!column.enabled) return null

      const saturatedData = saturatedColumns[column.key]

      // @ts-ignore
      const onClick = saturatedData?.onClick || undefined

      // @ts-ignore
      const className = saturatedData?.className || undefined

      // @ts-ignore
      const content = saturatedData?.content ?? saturatedData

      const width = column.width || 100
      const style: React.CSSProperties = { width: width }

      if (!onClick)
        return (
          <span key={index} className={`${styles.defaultColumn} ${className}`} style={style}>
            {content}
          </span>
        )

      return (
        <button key={index} onClick={onClick} className={`${styles.defaultColumn} ${className}`} style={style}>
          {content}
        </button>
      )
    })}
  </>
)
